import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const brandTextareaVariants = cva(
	"flex w-full rounded-md border bg-transparent px-3 py-2 text-base text-foreground transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-none",
	{
		variants: {
			variant: {
				default: "border-border",
				error: "border-destructive focus-visible:ring-destructive/40",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export interface BrandTextareaProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
		VariantProps<typeof brandTextareaVariants> {
	/** Minimum height in pixels (default: 100) */
	minHeight?: number;
	/** Maximum height in pixels (default: 300) */
	maxHeight?: number;
}

const BrandTextarea = React.forwardRef<HTMLTextAreaElement, BrandTextareaProps>(
	(
		{
			className,
			variant,
			minHeight = 100,
			maxHeight = 300,
			onChange,
			...props
		},
		ref,
	) => {
		const internalRef = React.useRef<HTMLTextAreaElement>(null);
		const textareaRef =
			(ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

		const adjustHeight = React.useCallback(() => {
			const textarea = textareaRef.current;
			if (!textarea) return;

			textarea.style.height = "auto";
			const newHeight = Math.min(
				Math.max(textarea.scrollHeight, minHeight),
				maxHeight,
			);
			textarea.style.height = `${newHeight}px`;
		}, [textareaRef, minHeight, maxHeight]);

		React.useLayoutEffect(() => {
			adjustHeight();
		}, [adjustHeight]);

		const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
			adjustHeight();
			onChange?.(e);
		};

		return (
			<textarea
				className={cn(brandTextareaVariants({ variant }), className)}
				ref={textareaRef}
				onChange={handleChange}
				style={{ minHeight, maxHeight, overflow: "auto" }}
				{...props}
			/>
		);
	},
);
BrandTextarea.displayName = "BrandTextarea";

export { BrandTextarea, brandTextareaVariants };
